/**
 * Pure state for the comment thread window: one session per open, discarded on
 * close, and never shared between two threads.
 *
 * A window opened from a pin hangs off that pin and is provisional — click
 * elsewhere on the board and it goes away. Dragging it somewhere is a
 * commitment, and one state covers both halves of it: the window detaches from
 * the pin, so moving the node no longer moves the window, and only the close
 * button ends it. Reopening starts over.
 */

export type ThreadWindowSession = {
  /** The thread this session was opened for; another thread starts a new one. */
  threadId: string;
  /** Screen-px displacement of the window from the point it hangs off. */
  offset: { x: number; y: number };
  /**
   * The pin position, in flow space, frozen at the drag that moved the window —
   * null while the window still hangs off the live pin. Non-null is the whole
   * of "the user has moved this": detached from the node, and dismissable only
   * by the close button.
   */
  detachedAt: { x: number; y: number } | null;
};

/** The session every open — and reopen — starts from. */
export function openThreadWindow(threadId: string): ThreadWindowSession {
  return { threadId, offset: { x: 0, y: 0 }, detachedAt: null };
}

/**
 * The session belonging to `threadId` — `session` itself only when it is that
 * thread's. Nothing from the window before this one carries into it: not the
 * offset, and not the drag that would have had it ignore its own clicks.
 */
export function threadWindowSessionFor(
  session: ThreadWindowSession | null,
  threadId: string,
): ThreadWindowSession {
  return session?.threadId === threadId ? session : openThreadWindow(threadId);
}

/** Screen-px a header drag must cover before it counts as repositioning. */
export const WINDOW_DRAG_SLOP = 3;

/**
 * A header drag, from the offset the grab started at, against the pin position
 * at the time. Below the slop the session is returned unchanged — identity
 * included, so a jittery grab neither detaches the window nor re-renders it.
 */
export function dragThreadWindow(
  session: ThreadWindowSession,
  origin: { x: number; y: number },
  delta: { x: number; y: number },
  pin: { x: number; y: number },
): ThreadWindowSession {
  const crossedSlop =
    Math.abs(delta.x) >= WINDOW_DRAG_SLOP ||
    Math.abs(delta.y) >= WINDOW_DRAG_SLOP;
  const detachedAt = session.detachedAt ?? (crossedSlop ? pin : null);
  if (!detachedAt) return session;
  return {
    threadId: session.threadId,
    offset: { x: origin.x + delta.x, y: origin.y + delta.y },
    detachedAt,
  };
}

/**
 * The flow-space point the window hangs its offset off: the live pin while
 * attached, and where the pin stood at the drag once detached. The pin itself
 * always follows its node — only the window stops.
 */
export function threadWindowOrigin(
  session: ThreadWindowSession,
  pin: { x: number; y: number },
): { x: number; y: number } {
  return session.detachedAt ?? pin;
}

/** Where a pointerdown landed, as far as the open window is concerned. */
export type ThreadWindowPointerTarget = {
  /** Inside the window's own DOM. */
  insideWindow: boolean;
  /**
   * A Liveblocks floating layer is up anywhere — emoji picker, mention
   * autocomplete, comment dropdown. They portal out of the window, so the DOM
   * says outside wherever the click lands, and this pointerdown is the one the
   * vendor closes them on.
   */
  floatingLayerOpen: boolean;
  /** On a comment pin, whose own click decides which thread is open. */
  onCommentPin: boolean;
};

/**
 * Dismissal is one layer at a time: an open popover takes the click that
 * closes it, and the window only answers the next one.
 */
export function dismissesThreadWindow(
  session: ThreadWindowSession,
  target: ThreadWindowPointerTarget,
): boolean {
  if (session.detachedAt) return false;
  if (target.floatingLayerOpen) return false;
  return !target.insideWindow && !target.onCommentPin;
}

/**
 * Whether a pin's click closes the open window instead of opening one. Only
 * its own thread's window, and only while that window still hangs off the pin:
 * moving it is the user saying keep this open, and from there the close button
 * is the only way out.
 *
 * The pin decides this on its pointerup, alone. `dismissesThreadWindow` sees
 * the pointerdown first and leaves pin clicks be — were it to close on one,
 * the pointerup behind it would open the thread straight back up.
 */
export function pinClickClosesThreadWindow(
  session: ThreadWindowSession | null,
  threadId: string,
): boolean {
  return !!session && session.threadId === threadId && !session.detachedAt;
}

/**
 * Reads a pointerdown's target as one of the three the window cares about.
 * Ancestry, never a captured element: an exiting window's ref detach lands
 * after the next window's attach, which had the classifier calling every click
 * in the second window an outside one.
 */
export function threadWindowPointerTarget(
  target: Element | null,
): ThreadWindowPointerTarget {
  return {
    insideWindow: !!target?.closest(`[${COMMENT_WINDOW_ATTRIBUTE}]`),
    // The vendor mounts a portal only while its layer is open, so the query is
    // the state: something is up, and this click is its.
    floatingLayerOpen: !!document.querySelector(LIVEBLOCKS_PORTAL_SELECTOR),
    onCommentPin: !!target?.closest(`[${COMMENT_PIN_ATTRIBUTE}]`),
  };
}

/** The class every Liveblocks floating layer carries — the vendor's own
 *  outside-click checks match on it. */
export const LIVEBLOCKS_PORTAL_SELECTOR = ".lb-portal";

/** Marks a pin in the pin layer, so a pointerdown can be traced back to one. */
export const COMMENT_PIN_ATTRIBUTE = "data-canvas-comment-pin";

/**
 * Marks the open window. Inside-ness is read off the event's own ancestry
 * rather than a captured element: an exiting window's ref detach lands after
 * the next one's attach, which left the classifier calling the new window's
 * every click an outside one.
 */
export const COMMENT_WINDOW_ATTRIBUTE = "data-canvas-comment-window";

/** The vendor's comment list inside a `Thread` — the window's scrolling part. */
export const THREAD_SCROLLER_SELECTOR = ".lb-thread-comments";
